import { EnquiryForm } from "@/components/EnquiryForm";
import { ArrowUpRight, Flame, MountainSnow, Sparkles, Heart, Users, Clock, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND, IMAGES, whatsappLink } from "@/lib/brand";
import { Reveal } from "@/components/Reveal";

// Curated imagery — sacred, cinematic, editorial (using verified brand assets)
const IMG = {
  hero: IMAGES.heroKedarnath,
  temple: IMAGES.chardham,        // Kedarnath temple bell tower
  mandap: IMAGES.mandap,          // Floral mandap
  couple: IMAGES.weddingCouple,   // Couple in mountains
  fire: IMAGES.bell,              // Temple bell / offering
  peaks: IMAGES.roomLuxury,       // Ambient scene
  garland: IMAGES.mandap,         // Mandap detail (reuse)
  ritual: IMAGES.temple,          // Temple
  moment: IMAGES.weddingCouple,   // Couple moment (reuse)
  breakfast: IMAGES.food,
};

const PACKAGES = [
  {
    tier: "Sacred Vow",
    hi: "पवित्र वचन",
    tagline: "An intimate ceremony held in reverence",
    guests: "Up to 25 guests",
    days: "2 nights · 3 days",
    from: "₹ 4,50,000",
    inclusions: [
      "Temple pheras with authorised Pandit-ji",
      "Havan samagri & Ganga-jal from Gaurikund",
      "Traditional floral mandap by the Mandakini",
      "Boutique stay at Kedarnath Cottage (Premium rooms)",
      "Curated satvik cuisine — 6 courses",
      "Documentation photography (6 hours)",
      "Bridal room turndown & mehendi setup",
    ],
    accent: false,
  },
  {
    tier: "Divine Union",
    hi: "दिव्य मिलन",
    tagline: "The signature Himalayan wedding experience",
    guests: "Up to 60 guests",
    days: "3 nights · 4 days",
    from: "₹ 9,75,000",
    inclusions: [
      "Full Triyuginarayan Temple ceremony with Akhand Dhuni parikrama",
      "Curated mandap with heritage florals, marigold & rhododendron",
      "Full stay for wedding party at Kedarnath Cottage",
      "Cinematic film + traditional photography (2 shooters, 2 days)",
      "Sangeet setup with live folk performance",
      "Vidaai & baraat coordination",
      "Personal wedding concierge & pandit coordination",
      "Char Dham darshan add-on optional",
    ],
    accent: true,
  },
  {
    tier: "Celestial Blessing",
    hi: "स्वर्गीय आशीर्वाद",
    tagline: "A grand destination celebration held with grace",
    guests: "Up to 120 guests",
    days: "4 nights · 5 days",
    from: "On request",
    inclusions: [
      "Exclusive buyout of Kedarnath Cottage & partner properties",
      "Bespoke mandap design with Himalayan floristry",
      "Pre-wedding shoot at Kedarnath & Sonprayag valleys",
      "Full-service culinary team — Pahadi thali, live counters",
      "Helicopter arrival for the couple (subject to weather)",
      "Décor for haldi, mehendi, sangeet, phera & vidaai",
      "Dedicated wedding director + 24×7 concierge team",
      "Personalised welcome hampers for every guest",
    ],
    accent: false,
  },
];

const TIMELINE = [
  { time: "Day 01", label: "Arrival & Welcome", note: "Chai by the river · haldi rituals · storyteller under the stars" },
  { time: "Day 02", label: "Mehendi & Sangeet", note: "Pahadi folk musicians · candlelit courtyard · guest feasting" },
  { time: "Day 03", label: "Phera at Triyuginarayan", label_hi: "फेरे", note: "Sacred vows before the Akhand Dhuni · parikrama · aarti" },
  { time: "Day 04", label: "Reception & Vidaai", note: "Slow farewell · a shared sunrise · departure with blessings" },
];

const FAQ = [
  {
    q: "Where is Triyuginarayan Temple?",
    a: "Triyuginarayan is a sacred village in the Rudraprayag district of Uttarakhand — believed to be the very site where Lord Shiva and Goddess Parvati exchanged their wedding vows. It is approximately 12 km from Sonprayag and around 25 km from our property, Kedarnath Cottage in Sersi.",
  },
  {
    q: "What is the Akhand Dhuni?",
    a: "The Akhand Dhuni is the eternal sacred fire that has been burning at Triyuginarayan Temple since the divine wedding of Shiva and Parvati. Couples take their pheras (sacred rounds) around this holy flame — considered the most auspicious blessing a marriage can receive.",
  },
  {
    q: "When is the best time to plan a wedding here?",
    a: "The most beautiful months are April–June and September–early November, when weather is clear, meadows bloom and skies stay open. We help you plan around auspicious muhurats and clear road conditions.",
  },
  {
    q: "Do you handle everything or only accommodation?",
    a: "We manage the complete experience — pandit coordination, temple permissions, mandap and florals, cuisine, photography, guest hospitality and logistics. You arrive with your family; we hold every detail with care.",
  },
  {
    q: "How many guests can attend the ceremony?",
    a: "Triyuginarayan Temple accommodates intimate ceremonies. We recommend keeping the immediate ritual party under 60 guests, with a wider reception held at Kedarnath Cottage. Larger celebrations up to 120 are possible with our Celestial Blessing package.",
  },
];

export default function Triyuginarayan() {
  return (
    <div data-testid="page-triyuginarayan" className="bg-kc">
      {/* ============ HERO ============ */}
      <section className="relative h-[100vh] min-h-[720px] overflow-hidden">
        <img src={IMG.hero} alt="Triyuginarayan Temple in the Himalayas at dawn" className="absolute inset-0 w-full h-full object-cover kc-hero-image" />
        <div className="absolute inset-0" style={{background: "linear-gradient(180deg, rgba(11,11,12,0.4) 0%, rgba(11,11,12,0.15) 45%, rgba(11,11,12,0.92) 100%)"}} />

        {/* Editorial side label */}
        <div className="hidden lg:block absolute left-8 top-1/2 -translate-y-1/2 z-10">
          <div className="vertical-label">Triyuginarayan · त्रियुगीनारायण</div>
        </div>

        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 text-[#F8F6F2]">
          <div className="kc-ornament max-w-md kc-fade-up">A Wedding at the Origin of All Weddings</div>
          <h1 className="h-display text-[46px] sm:text-[64px] md:text-[84px] lg:text-[104px] font-light leading-[0.98] mt-8 max-w-6xl kc-fade-up kc-delay-1" style={{textShadow: "0 2px 32px rgba(0,0,0,0.45)"}}>
            Where Shiva and Parvati <br />
            <span className="italic text-[#D4AF37]">took their vows.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-[15px] md:text-[17px] leading-[1.7] text-[#F8F6F2]/85 kc-fade-up kc-delay-2 font-light">
            The Akhand Dhuni still burns at Triyuginarayan — the eternal flame that witnessed the divine wedding. Exchange your vows before the same sacred fire, in the lap of the Himalayas.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 kc-fade-up kc-delay-3">
            <a href="#enquire" className="btn-gold" data-testid="tri-hero-cta-plan">Plan My Sacred Wedding <ArrowUpRight size={14} /></a>
            <a href="#packages" className="btn-ghost-light" data-testid="tri-hero-cta-packages">View Wedding Packages</a>
          </div>

          {/* Bottom meta row */}
          <div className="mt-16 pt-8 border-t border-[#F8F6F2]/15 grid grid-cols-2 md:grid-cols-4 gap-8 kc-fade-up kc-delay-4">
            {[
              { k: "Altitude", v: "1,980 m" },
              { k: "From Cottage", v: "25 km · 1h drive" },
              { k: "Best Season", v: "Apr – Nov" },
              { k: "Sacred Since", v: "Satya Yuga" },
            ].map((m) => (
              <div key={m.k}>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{m.k}</div>
                <div className="font-serif-display text-2xl md:text-3xl mt-3 font-light">{m.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE LEGEND ============ */}
      <section className="section-pad bg-kc">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10 items-start">
          <Reveal className="col-span-12 md:col-span-5">
            <div className="overline">The Sacred Legend</div>
            <h2 className="font-serif-display text-4xl md:text-6xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
              Three yugas ago, <br /><span className="italic text-gold">the wedding began here.</span>
            </h2>
            <div className="font-devanagari text-lg text-ink-soft mt-6">तीन युगों से जल रही अखंड धूनी</div>
          </Reveal>

          <Reveal className="col-span-12 md:col-span-6 md:col-start-7 space-y-6" delay={200}>
            <p className="text-[16px] leading-[1.85] text-ink-soft">
              At Triyuginarayan, Lord Vishnu witnessed the divine union of Lord Shiva and Goddess Parvati as Brahma-ji performed the sacred rites. The Akhand Dhuni — the eternal flame kindled at that wedding — has burned continuously for three yugas.
            </p>
            <p className="text-[16px] leading-[1.85] text-ink-soft">
              Even today, couples travel from across India to take their pheras around this same flame. It is believed that vows exchanged here carry the blessings of Shiva-Parvati themselves — an eternal promise, held together by an eternal fire.
            </p>
            <div className="pt-6 flex flex-wrap gap-8">
              <div className="flex items-start gap-4">
                <Flame size={28} className="text-gold mt-1" strokeWidth={1.2} />
                <div>
                  <div className="meta">Akhand Dhuni</div>
                  <div className="font-serif-display text-2xl mt-1">The Eternal Flame</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MountainSnow size={28} className="text-gold mt-1" strokeWidth={1.2} />
                <div>
                  <div className="meta">Setting</div>
                  <div className="font-serif-display text-2xl mt-1">Himalayan Meadow</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ EDITORIAL IMAGE STRIP ============ */}
      <section className="pb-24">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-7 img-zoom">
            <img src={IMAGES.chardham} alt="Kedarnath temple sanctum" className="w-full h-[520px] object-cover" />
          </div>
          <div className="col-span-12 md:col-span-5 img-zoom">
            <img src={IMAGES.mandap} alt="Floral mandap ceremony" className="w-full h-[520px] object-cover" />
          </div>
          <div className="col-span-6 md:col-span-4 img-zoom">
            <img src={IMAGES.weddingCouple} alt="Bride and groom in the Himalayas" className="w-full h-[340px] object-cover" />
          </div>
          <div className="col-span-6 md:col-span-4 img-zoom">
            <img src={IMAGES.bell} alt="Temple bell offering" className="w-full h-[340px] object-cover" />
          </div>
          <div className="col-span-12 md:col-span-4 img-zoom">
            <img src={IMAGES.heroKedarnath} alt="Himalayan peaks at dawn" className="w-full h-[340px] object-cover" />
          </div>
        </div>
      </section>

      {/* ============ VENUE & MANDAP INFO ============ */}
      <section className="section-pad bg-black-lux text-[#F8F6F2]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <Reveal className="grid grid-cols-12 gap-10 items-end mb-20">
            <div className="col-span-12 md:col-span-7">
              <div className="overline text-gold">Venue Character</div>
              <h2 className="font-serif-display text-5xl md:text-7xl mt-6 font-light leading-[1] tracking-[-0.02em]">
                The venue is a temple. <br /><span className="italic text-gold">The mandap is a mountain.</span>
              </h2>
            </div>
            <p className="col-span-12 md:col-span-4 text-[15px] leading-[1.85] text-[#F8F6F2]/75">
              Ceremonies unfold either inside the temple courtyard — around the Akhand Dhuni — or in the surrounding alpine meadow with the Himalayas as your witness. Every detail is arranged with reverence for the sanctity of the site.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D4AF37]/15">
            {[
              { icon: Flame, title: "Temple Courtyard", desc: "Intimate ceremonies with pheras around the eternal Akhand Dhuni. Traditional, sacred and deeply personal." },
              { icon: MountainSnow, title: "Alpine Meadow", desc: "Open-air mandap with Chaukhamba peaks in the frame. Ideal for photography and larger gatherings up to 120." },
              { icon: Sparkles, title: "Riverside Reception", desc: "Reception held at Kedarnath Cottage in Sersi — by the Mandakini river, under a canopy of stars." },
            ].map(({ icon: Icon, title, desc }, i) => (
              <div key={title} className="bg-black-lux p-10 md:p-12" data-testid={`tri-venue-${i}`}>
                <Icon size={32} className="text-gold" strokeWidth={1.2} />
                <div className="font-serif-display text-3xl mt-8 font-light">{title}</div>
                <p className="mt-4 text-[14px] leading-[1.75] text-[#F8F6F2]/70">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PACKAGES ============ */}
      <section id="packages" className="section-pad bg-kc">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <Reveal className="grid grid-cols-12 gap-10 items-end mb-20">
            <div className="col-span-12 md:col-span-7">
              <div className="overline">Wedding Collections</div>
              <h2 className="font-serif-display text-5xl md:text-7xl mt-6 font-light leading-[1] tracking-[-0.02em]">
                Three curated ways <br /><span className="italic text-gold">to say forever.</span>
              </h2>
            </div>
            <p className="col-span-12 md:col-span-4 text-[15px] leading-[1.85] text-ink-soft">
              Each collection is a starting point — every wedding at THE KC INDIA is tailored personally to the couple, the family and the auspicious muhurat.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.tier} delay={i * 120} className={`flex ${p.accent ? "md:-translate-y-4" : ""}`}>
                <div
                  data-testid={`tri-package-${i}`}
                  className={`flex flex-col w-full p-10 md:p-12 ${
                    p.accent
                      ? "bg-black-lux text-[#F8F6F2] border border-[#D4AF37]"
                      : "bg-surface border border-kc"
                  }`}
                >
                  <div className={`text-[10px] uppercase tracking-[0.32em] ${p.accent ? "text-gold" : "text-gold"}`}>№ 0{i + 1} · Collection</div>
                  <div className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-tight">{p.tier}</div>
                  <div className={`font-devanagari text-base mt-2 ${p.accent ? "text-[#F8F6F2]/60" : "text-ink-mute"}`}>{p.hi}</div>

                  <p className={`mt-6 text-[14px] leading-[1.7] ${p.accent ? "text-[#F8F6F2]/75" : "text-ink-soft"}`}>{p.tagline}</p>

                  <div className={`mt-8 pt-6 border-t ${p.accent ? "border-[#D4AF37]/25" : "border-kc"} grid grid-cols-2 gap-4`}>
                    <div>
                      <div className={`text-[9px] uppercase tracking-[0.28em] ${p.accent ? "text-gold" : "text-gold"}`}>Guests</div>
                      <div className="font-serif-display text-xl mt-2">{p.guests}</div>
                    </div>
                    <div>
                      <div className={`text-[9px] uppercase tracking-[0.28em] ${p.accent ? "text-gold" : "text-gold"}`}>Duration</div>
                      <div className="font-serif-display text-xl mt-2">{p.days}</div>
                    </div>
                  </div>

                  <ul className={`mt-8 space-y-3 text-[13.5px] leading-[1.6] ${p.accent ? "text-[#F8F6F2]/80" : "text-ink-soft"}`}>
                    {p.inclusions.map((inc, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className={`mt-2 w-1 h-1 flex-shrink-0 ${p.accent ? "bg-gold" : "bg-gold"}`} />
                        <span>{inc}</span>
                      </li>
                    ))}
                  </ul>

                  <div className={`mt-10 pt-8 border-t ${p.accent ? "border-[#D4AF37]/25" : "border-kc"} flex items-baseline gap-3`}>
                    <div className={`text-[10px] uppercase tracking-[0.28em] ${p.accent ? "text-gold" : "text-gold"}`}>From</div>
                    <div className="font-serif-display text-3xl">{p.from}</div>
                  </div>

                  <a
                    href="#enquire"
                    className={`mt-8 ${p.accent ? "btn-gold" : "btn-saffron"} w-full justify-center`}
                    data-testid={`tri-package-cta-${i}`}
                  >
                    Enquire · {p.tier} <ArrowUpRight size={14} />
                  </a>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="mt-10 text-[12px] text-ink-mute text-center max-w-2xl mx-auto leading-relaxed">
            All packages are indicative starting points. Final quotations are prepared personally after understanding your family, guest count, dates and preferences. Prices are exclusive of GST and government temple permissions.
          </p>
        </div>
      </section>

      {/* ============ THE 4-DAY EXPERIENCE ============ */}
      <section className="section-pad bg-cream">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <Reveal className="mb-20 max-w-3xl">
            <div className="overline">The Divine Union Experience</div>
            <h2 className="font-serif-display text-5xl md:text-7xl mt-6 font-light leading-[1] tracking-[-0.02em]">
              Four days that hold <br /><span className="italic text-gold">a lifetime.</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-ink/10">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.time} delay={i * 100} className="bg-cream p-8 md:p-10">
                <div className="index-num">{t.time}</div>
                <div className="font-serif-display text-3xl mt-6 leading-tight">{t.label}</div>
                {t.label_hi && <div className="font-devanagari text-base text-ink-mute mt-2">{t.label_hi}</div>}
                <p className="mt-6 text-[13.5px] leading-[1.7] text-ink-soft">{t.note}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY THE KC INDIA ============ */}
      <section className="section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10">
          <Reveal className="col-span-12 md:col-span-5">
            <div className="img-zoom">
              <img src={IMG.couple} alt="A wedded couple in the Himalayas" className="w-full h-[640px] object-cover" />
            </div>
          </Reveal>

          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <Reveal>
              <div className="overline">Why THE KC INDIA</div>
              <h2 className="font-serif-display text-5xl md:text-6xl mt-6 font-light leading-[1] tracking-[-0.02em]">
                We do not host weddings. <br /><span className="italic text-gold">We hold them.</span>
              </h2>
            </Reveal>

            <div className="mt-12 space-y-8">
              {[
                { icon: Heart, t: "One family per week", d: "We host only one wedding at a time. Your celebration is never shared, never rushed." },
                { icon: MapPin, t: "25 km from the temple", d: "Kedarnath Cottage in Sersi is the closest luxury property to Triyuginarayan — a peaceful base for your family." },
                { icon: Users, t: "Every detail, personally held", d: "A dedicated wedding director walks with you from first call to final vidaai." },
                { icon: Clock, t: "Muhurat with meaning", d: "We coordinate with authorised temple pandits so your pheras happen at the most auspicious moment." },
              ].map(({ icon: Icon, t, d }, i) => (
                <Reveal key={t} delay={i * 100} className="flex gap-6 pb-8 border-b border-kc">
                  <Icon size={26} className="text-gold flex-shrink-0 mt-1" strokeWidth={1.2} />
                  <div>
                    <div className="font-serif-display text-2xl">{t}</div>
                    <p className="mt-3 text-[14px] leading-[1.75] text-ink-soft">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="section-pad bg-kc">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10">
          <Reveal className="col-span-12 md:col-span-4">
            <div className="overline">Frequently Asked</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-6 font-light leading-[1] tracking-[-0.02em]">
              Answered <span className="italic text-gold">with care.</span>
            </h2>
            <p className="mt-8 text-[14px] leading-[1.8] text-ink-soft">
              Something not covered here? Call us at {BRAND.phone} or write to {BRAND.email}. We reply personally, always.
            </p>
          </Reveal>

          <div className="col-span-12 md:col-span-7 md:col-start-6 divide-y divide-[color:var(--kc-line)] border-t border-b border-kc">
            {FAQ.map((f, i) => (
              <details key={i} className="group py-8" data-testid={`tri-faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <span className="font-serif-display text-2xl md:text-3xl font-light leading-tight">{f.q}</span>
                  <span className="index-num flex-shrink-0 mt-2 group-open:rotate-45 transition-transform duration-500">+</span>
                </summary>
                <p className="mt-6 text-[15px] leading-[1.85] text-ink-soft max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ENQUIRY ============ */}
      <section id="enquire" className="bg-black-lux text-[#F8F6F2] section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <Reveal>
              <div className="overline text-gold">Begin the Conversation</div>
              <h2 className="font-serif-display text-5xl md:text-6xl mt-6 font-light leading-[1] tracking-[-0.02em]">
                Your Triyuginarayan wedding <br /><span className="italic text-gold">begins with a hello.</span>
              </h2>
              <div className="font-devanagari text-lg mt-6 text-[#F8F6F2]/60">आपकी दिव्य यात्रा यहाँ से आरंभ होती है</div>
              <p className="mt-8 text-[15px] leading-[1.85] text-[#F8F6F2]/75 max-w-md">
                Share a few details and our wedding director will call you personally within 10 minutes. We will send you sample lookbooks, real couple stories and a private plan tailored to your family.
              </p>

              <div className="mt-12 space-y-5 pt-8 border-t border-[#D4AF37]/25">
                <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="flex items-center gap-4 text-[#F8F6F2] hover:text-gold transition-colors" data-testid="tri-call">
                  <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center"><span className="text-gold">📞</span></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Call directly</div>
                    <div className="font-serif-display text-xl mt-1">{BRAND.phone}</div>
                  </div>
                </a>
                <a href={whatsappLink("Namaste 🙏 I would like to plan my wedding at Triyuginarayan with THE KC INDIA.")} target="_blank" rel="noreferrer" className="flex items-center gap-4 text-[#F8F6F2] hover:text-gold transition-colors" data-testid="tri-whatsapp">
                  <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center"><span className="text-gold">💬</span></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold">WhatsApp us</div>
                    <div className="font-serif-display text-xl mt-1">Chat with a Wedding Director</div>
                  </div>
                </a>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-7">
            <EnquiryForm type="wedding" dark initial={{ city: "" }} />
          </div>
        </div>
      </section>

      {/* ============ CLOSING QUOTE ============ */}
      <section className="py-32 bg-kc">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="kc-ornament justify-center">Blessing</div>
          <p className="font-serif-italic text-3xl md:text-5xl mt-10 leading-[1.25] text-ink font-light">
            "The eternal flame does not remember every wedding it has blessed. <br className="hidden md:block" />But every wedding it blesses <span className="text-gold">remembers the flame.</span>"
          </p>
          <div className="font-devanagari text-lg text-ink-mute mt-10">— अखंड धूनी की छाया में</div>
        </div>
      </section>
    </div>
  );
}
